import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const WorkspaceDeleteDialog = ({ open, mode, workspace, onClose, onConfirm, isProcessing }) => {
  if (!workspace) {
    return null;
  }

  const isHardDelete = mode === 'hard';
  const title = isHardDelete ? 'Eliminación total del espacio' : 'Eliminar espacio (soft)';
  const actionLabel = isHardDelete ? 'Eliminar definitivamente' : 'Confirmar eliminación soft';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--wb-bg-surface)',
          border: '1px solid var(--wb-x-border-f8717140, rgba(248, 113, 113, 0.25))'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--wb-text-title)', display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon sx={{ color: 'var(--wb-danger)' }} />
        {title}
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'var(--wb-danger-18)' }}>
        <Typography variant="body2" sx={{ color: 'var(--wb-text)', mb: 2 }}>
          Estás gestionando el espacio <strong>{workspace.name || workspace.semantic_workspace_id}</strong>.
        </Typography>
        {isHardDelete ? (
          <>
            <Typography variant="body2" sx={{ color: 'var(--wb-x-text-fda4af, #fda4af)', mb: 1.5 }}>
              Esta acción eliminará definitivamente el espacio de análisis del sistema y no se podrá revertir.
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--wb-x-text-f87171cc, rgba(248, 113, 113, 0.8))' }}>
              Asegúrate de haber realizado previamente la eliminación soft y de que ningún equipo lo esté utilizando.
            </Typography>
          </>
        ) : (
          <Typography variant="body2" sx={{ color: 'var(--wb-x-text-f87171cc, rgba(248, 113, 113, 0.8))' }}>
            El espacio quedará inactivo y marcado como eliminado. Podrás restaurarlo en cualquier momento desde este panel.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isProcessing} sx={{ color: 'var(--wb-muted)' }}>
          Cancelar
        </Button>
        <Button
          onClick={() => onConfirm?.(workspace)}
          variant="contained"
          disabled={isProcessing}
          sx={{
            bgcolor: isHardDelete ? 'var(--wb-x-bg-ef4444, #ef4444)' : 'var(--wb-x-bg-f97316, #f97316)',
            '&:hover': { bgcolor: isHardDelete ? 'var(--wb-x-bg-dc2626, #dc2626)' : 'var(--wb-x-bg-ea580c, #ea580c)' }
          }}
        >
          {isProcessing ? 'Procesando…' : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WorkspaceDeleteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['soft', 'hard']).isRequired,
  workspace: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool
};

WorkspaceDeleteDialog.defaultProps = {
  workspace: null,
  isProcessing: false
};

export default WorkspaceDeleteDialog;
